import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Compass, RotateCcw, ListChecks, GraduationCap, ChevronRight, Sparkles, PlayCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";

const ARTICLES = [
  { slug: "welcome", title: "Welcome to BroadcastFlow AI",
    body: "BroadcastFlow AI is your all-in-one radio content platform. From the Control Room you can watch station activity, credits, and scheduled content. The sidebar groups everything by workflow: Content Creation for articles and news, Studio City for audio/video production, Live & On-Air for DJ tools, Distribution for social publishing, and Station Operations for your team and analytics. Start with the guided tour below, then work through the Getting Started checklist on your dashboard." },
  { slug: "add-your-first-station", title: "Add Your First Station",
    body: "Head to the setup wizard (dashboard checklist → Resume setup) or Station Operations → Station. Give your station a name, format, tagline, and target audience — this powers every AI generation, so richer details mean better content. You can add more stations later from the same place, subject to your plan." },
  { slug: "connect-your-stream", title: "Connect Your Stream",
    body: "In the setup wizard step 4 (or Station → Streams) pick your provider — Shoutcast, Icecast, AzuraCast, SAM Broadcaster, Radio.co, Live365 and more — then paste your stream or metadata URL. Use the built-in test button to confirm BroadcastFlow AI can read your Now Playing data. The Stream URL is the internet address we use to retrieve live song information from your broadcast." },
  { slug: "connect-social-media", title: "Connect Social Media",
    body: "Open Distribution → Social Connections and link Facebook, Instagram, X, TikTok, YouTube, LinkedIn, Telegram, Discord, Bluesky and more. Once connected, every studio can queue posts to your networks, and the auto-poster can share Now Playing updates and news on a schedule you control." },
  { slug: "create-your-first-ai-post", title: "Create Your First AI Post",
    body: "Open Studio City → AI Studio, pick a content type (welcome post, promo, station description…), and generate. Review, tweak, and save to your Content Library or queue it straight to your socials. Your station profile and content preferences shape the tone automatically." },
  { slug: "understanding-credits", title: "Understanding Credits",
    body: "AI generations use credits. Watch your balance in the sidebar AI Usage meter or Account → Billing. Your plan includes a monthly allowance; you can buy extra credits anytime or bring your own API keys (AI Providers) to run generations on your own accounts." },
  { slug: "create-your-first-podcast", title: "Create Your First Podcast",
    body: "Open Studio City → Podcast Studio → Podcast Creator. Upload audio or reuse a broadcast, and the AI generates titles, show notes, and descriptions. Episodes live in the Podcast Vault, complete with RSS feeds ready for directories." },
  { slug: "intro-music-video-studio", title: "Introduction to Music Video Studio",
    body: "Vision Studio (Studio City → Vision Studio) turns finished songs into visuals — concepts, storyboards, AI-generated scenes, visualizers and short promo clips. Create a project, upload your track, and walk through the guided production flow." },
  { slug: "intro-digital-magazine-studio", title: "Introduction to Digital Magazine Studio",
    body: "The Digital Magazine Studio gives your station a branded online publication — articles, artist features, interviews, categories, covers and newsletters, plus an AI Writer for fast drafts. Find it in the sidebar under Digital Magazine (Network plan)." },
];

const embedFor = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (yt) return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = url.match(/vimeo\.com\/(\d{6,})/);
  if (vm) return { kind: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  return { kind: "video", src: url };
};

const GuideVideo = ({ video }) => {
  const emb = embedFor(video?.video_url);
  if (!emb) return null;
  return (
    <div className="mb-3">
      <div className="overflow-hidden rounded-lg border border-line aspect-video bg-black">
        {emb.kind === "iframe" ? (
          <iframe src={emb.src} title="Walkthrough video" className="h-full w-full" allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
        ) : (
          <video src={emb.src} controls className="h-full w-full" preload="metadata" />
        )}
      </div>
      {video.caption && <div className="mt-1 text-[10px] text-ink-muted">{video.caption}</div>}
    </div>
  );
};

export default function HelpGettingStarted() {
  const [beginner, setBeginner] = useState(null);
  const [busy, setBusy] = useState("");
  const [videos, setVideos] = useState({});

  useEffect(() => {
    api.get("/tour/bootstrap").then((r) => setBeginner(r.data.beginner_mode)).catch(() => {});
    api.get("/tour/guide-videos").then((r) => setVideos(r.data.videos || {})).catch(() => {});
  }, []);

  const restartTour = async () => {
    setBusy("tour");
    window.dispatchEvent(new CustomEvent("bfa:start-tour"));
    setBusy("");
  };

  const resetChecklist = async () => {
    setBusy("checklist");
    try {
      await api.post("/tour/checklist-visibility", { hidden: false });
      localStorage.setItem("bfa_show_checklist", "1");
      localStorage.setItem("bfa_checklist_collapsed", "0");
      toast.success("Checklist reset — it's back on your dashboard");
    } catch { toast.error("Couldn't reset the checklist"); }
    setBusy("");
  };

  const toggleBeginner = async (on) => {
    setBeginner(on);
    try {
      await api.put("/tour/beginner-mode", { enabled: on });
      toast.success(on ? "Beginner Mode on — extra tips enabled" : "Beginner Mode off");
    } catch { toast.error("Couldn't save preference"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto space-y-6" data-testid="getting-started-page">
      <PageHeader eyebrow="// HELP · GETTING STARTED" title="Getting Started"
        description="Everything you need to go from first login to your first published content." />

      <div className="grid sm:grid-cols-2 gap-3">
        <button onClick={restartTour} disabled={busy === "tour"} data-testid="gs-restart-tour"
          className="rounded-xl border border-brand-amber/40 bg-brand-amber/10 p-5 text-left hover:bg-brand-amber/15 transition">
          <Compass className="h-5 w-5 text-brand-amber mb-2" />
          <div className="text-sm font-bold text-ink-primary">Restart Guided Tour</div>
          <div className="text-[11px] text-ink-muted mt-1">Take the spotlight walkthrough of every section again.</div>
        </button>
        <button onClick={resetChecklist} disabled={busy === "checklist"} data-testid="gs-reset-checklist"
          className="rounded-xl border border-line bg-bg-surface p-5 text-left hover:border-brand-cyan/50 transition">
          <ListChecks className="h-5 w-5 text-brand-cyan mb-2" />
          <div className="text-sm font-bold text-ink-primary">Reset Getting Started Checklist</div>
          <div className="text-[11px] text-ink-muted mt-1">Bring the setup checklist back onto your dashboard.</div>
        </button>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="gs-preferences">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold text-ink-primary">
              <GraduationCap className="h-4 w-4 text-brand-amber" /> Beginner Mode
            </div>
            <p className="text-[11px] text-ink-muted mt-1 max-w-lg">
              ON: show extra explanations, tooltips, setup tips, and recommended next steps.
              OFF: the standard streamlined interface. Automatically enabled for new accounts.
            </p>
          </div>
          {beginner !== null && (
            <input type="checkbox" checked={!!beginner} data-testid="gs-beginner-toggle"
              onChange={(e) => toggleBeginner(e.target.checked)} className="h-4 w-4" />
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-brand-amber" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Getting Started guides</span>
        </div>
        <Accordion type="single" collapsible className="space-y-2" data-testid="gs-articles">
          {ARTICLES.map((a) => (
            <AccordionItem key={a.slug} value={a.slug} id={a.slug}
              className="rounded-xl border border-line bg-bg-surface px-4">
              <AccordionTrigger className="text-sm font-semibold text-ink-primary hover:text-brand-amber py-3.5"
                data-testid={`gs-article-${a.slug}`}>
                <span className="flex items-center gap-2">
                  {a.title}
                  {videos[a.slug] && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-brand-amber/40 bg-brand-amber/10 px-2 py-0.5 text-[9px] font-bold text-brand-amber"
                      data-testid={`gs-video-badge-${a.slug}`}>
                      <PlayCircle className="h-3 w-3" /> VIDEO
                    </span>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-xs text-ink-secondary leading-relaxed pb-4">
                {videos[a.slug] && <GuideVideo video={videos[a.slug]} />}
                {a.body}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <Link to="/dashboard/help" data-testid="gs-back-help"
        className="inline-flex items-center gap-1 text-xs font-bold text-ink-muted hover:text-ink-primary transition">
        Browse the full Help Center <ChevronRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
